<template>
  <ReportOverlayDialog
    :open="open"
    :title="`Notes — ${brandName} / ${periodKey}`"
    size="md"
    @close="$emit('close')"
  >
    <div class="sla-notes-dialog__body">
      <p class="sla-notes-dialog__meta text-muted">
        {{ flagLabel }} · {{ baseDate }} · {{ periodicity }}
      </p>

      <div
        v-if="loading"
        class="text-muted py-3"
      >
        Loading notes…
      </div>
      <div
        v-else-if="error"
        class="alert alert-danger"
      >
        {{ error }}
      </div>
      <div
        v-else-if="!notes.length"
        class="text-muted py-2"
      >
        No notes yet. Add the first comment below.
      </div>
      <ul
        v-else
        class="sla-notes-dialog__list"
      >
        <li
          v-for="note in notes"
          :key="note.id"
          class="sla-notes-dialog__item"
        >
          <div class="sla-notes-dialog__item-head">
            <strong>{{ note.authorName || note.authorEmail || 'User' }}</strong>
            <span class="text-muted">{{ formatTs(note.createdAt) }}</span>
          </div>
          <div class="sla-notes-dialog__item-body">
            {{ note.body }}
          </div>
        </li>
      </ul>

      <div class="sla-notes-dialog__composer">
        <label class="selectbox-headers">
          Add note
        </label>
        <textarea
          v-model="draft"
          class="sla-notes-dialog__textarea"
          rows="3"
          maxlength="4000"
          placeholder="Write a comment…"
          :disabled="saving"
        />
        <div class="sla-notes-dialog__actions">
          <span class="text-muted small">
            {{ draft.length }} / 4000
          </span>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="saving || !draft.trim()"
            @click="submit"
          >
            {{ saving ? 'Saving…' : 'Save note' }}
          </button>
        </div>
        <div
          v-if="saveError"
          class="alert alert-danger mt-2 mb-0"
        >
          {{ saveError }}
        </div>
      </div>
    </div>
  </ReportOverlayDialog>
</template>

<script>
import { mapActions } from 'vuex';
import ReportOverlayDialog from '@/components/Reports/ReportOverlayDialog.vue';

export default {
  name: 'SlaNotesDialog',
  components: { ReportOverlayDialog },
  props: {
    open: { type: Boolean, default: false },
    periodicity: { type: String, required: true },
    baseDate: { type: String, required: true },
    flag: { type: String, required: true },
    brandName: { type: String, required: true },
    periodKey: { type: String, required: true },
  },
  emits: [ 'close', 'saved' ],
  data() {
    return {
      notes: [],
      draft: '',
      loading: false,
      saving: false,
      error: null,
      saveError: null,
    };
  },
  computed: {
    flagLabel() {
      return this.flag === 'LTM' ? 'LTM' : 'E-commerce';
    },
  },
  watch: {
    open: {
      immediate: true,
      handler(val) {
        if (val) {
          this.draft = '';
          this.saveError = null;
          this.loadNotes();
        }
      },
    },
  },
  methods: {
    ...mapActions({
      fetchNotes: 'slaManagement/fetchNotes',
      createNote: 'slaManagement/createNote',
    }),
    async loadNotes() {
      this.loading = true;
      this.error = null;
      try {
        this.notes = await this.fetchNotes({
          periodicity: this.periodicity,
          baseDate: this.baseDate,
          flag: this.flag,
          brandName: this.brandName,
          periodKey: this.periodKey,
        });
      } catch (e) {
        this.error = e?.message || 'Failed to load notes';
        this.notes = [];
      } finally {
        this.loading = false;
      }
    },
    async submit() {
      const body = (this.draft || '').trim();
      if (!body) return;
      this.saving = true;
      this.saveError = null;
      try {
        const created = await this.createNote({
          periodicity: this.periodicity,
          baseDate: this.baseDate,
          flag: this.flag,
          brandName: this.brandName,
          periodKey: this.periodKey,
          body,
        });
        this.notes = [ ...this.notes, created ];
        this.draft = '';
        this.$emit('saved');
      } catch (e) {
        this.saveError = e?.message || 'Failed to save note';
      } finally {
        this.saving = false;
      }
    },
    formatTs(iso) {
      if (!iso) return '';
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return String(iso);
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const yyyy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, '0');
      const mi = String(d.getMinutes()).padStart(2, '0');
      return `${mm}/${dd}/${yyyy} ${hh}:${mi}`;
    },
  },
};
</script>
